import React, { useState } from "react";
import styled from "styled-components";

import { roleImageUrl } from "../../../utils/roleAssets";

interface RoleThumbnailProps {
  roleId: number;
  /**
   * サムネイルの**幅** (px)。実際の高さは `width * ASPECT_RATIO` で求まる縦長カード。
   * カード画像 (`card_role_<id>.png`) の元アスペクト比 1:1.414 (≈ 1/√2、A 縦) に合わせる。
   */
  size?: number;
  /** 右下に ID（"#1" など）を表示するかどうか */
  showIdBadge?: boolean;
}

/** カード画像の元アスペクト比 height / width。`1.414 ≈ √2` (A 縦比) */
const ASPECT_RATIO = 1.414;

const Wrapper = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size * ASPECT_RATIO}px;
  flex: 0 0 auto;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--soremo-border);
  background: var(--soremo-light-gray);
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Fallback = styled.div.attrs({ className: "u-row-center u-w100" })`
  height: 100%;
  color: #fff;
  font-family: "din-2014", sans-serif;
  font-weight: 800;
  font-size: 14px;
`;

const IdBadge = styled.span.attrs({ className: "bodytext-11" })`
  position: absolute;
  right: 2px;
  bottom: 2px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border-radius: 2px;
  padding: 0 4px;
  line-height: 14px;
  pointer-events: none;
`;

/**
 * ロール ID に紐付くサムネイル（`card_role_<id>.png`）を表示する小コンポーネント。
 * 画像が見つからない ID では ID 番号を中央に出すフォールバック表示。
 */
const RoleThumbnail: React.FC<RoleThumbnailProps> = ({
  roleId,
  size = 40,
  showIdBadge = true,
}) => {
  const [errored, setErrored] = useState(false);
  const src = roleImageUrl(roleId);

  return (
    <Wrapper $size={size}>
      {errored ? (
        <Fallback>#{roleId}</Fallback>
      ) : (
        <Image
          src={src}
          alt={`Role ${roleId} thumbnail`}
          onError={() => setErrored(true)}
        />
      )}
      {showIdBadge && <IdBadge>#{roleId}</IdBadge>}
    </Wrapper>
  );
};

export default RoleThumbnail;
